import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Action, History, Location } from 'history';

export interface UnsavedChangesNavigationGuardOptions {
  isUnsaved(): boolean;
  confirmLeave(): Promise<boolean>;
}

/**
 * Intercepts every router transition (sidebar links, browser back/forward, programmatic pushes) while
 * `isUnsaved()` is true and lets `confirmLeave` decide whether it goes through.
 *
 * history v4's blocker is synchronous, so the transition is cancelled first and replayed once the async
 * confirmation resolves. The guard stays registered after a confirmed leave: a route that only changes a
 * param keeps the page mounted, and its next form must be protected too.
 *
 * Returns the unregister callback.
 */
export function guardNavigationWhileUnsaved(
  history: History,
  { isUnsaved, confirmLeave }: UnsavedChangesNavigationGuardOptions,
): () => void {
  let replayingConfirmedTransition = false;
  let awaitingConfirmation = false;

  function replayTransition(location: Location, action: Action): void {
    replayingConfirmedTransition = true;
    try {
      if (action === 'REPLACE') {
        history.replace(location);
      } else {
        // A blocked POP has already been reverted by history, so the target is pushed rather than
        // re-popped: the browser API cannot tell how many entries back (or forward) the user went.
        history.push(location);
      }
    } finally {
      replayingConfirmedTransition = false;
    }
  }

  async function askAndReplay(location: Location, action: Action): Promise<void> {
    awaitingConfirmation = true;
    try {
      if (await confirmLeave()) {
        replayTransition(location, action);
      }
    } finally {
      awaitingConfirmation = false;
    }
  }

  return history.block((location, action) => {
    if (replayingConfirmedTransition || !isUnsaved()) {
      return;
    }
    if (!awaitingConfirmation) {
      void askAndReplay(location, action);
    }
    return false;
  });
}

export default function useUnsavedChangesNavigationGuard(options: UnsavedChangesNavigationGuardOptions): void {
  const history = useHistory();
  // Registered once per history; the callbacks are read through the ref so the blocker always sees the
  // caller's latest closure (the confirmation text names the record currently open).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(
    () =>
      guardNavigationWhileUnsaved(history, {
        isUnsaved: (): boolean => optionsRef.current.isUnsaved(),
        confirmLeave: (): Promise<boolean> => optionsRef.current.confirmLeave(),
      }),
    [history],
  );
}
